import { SERVICE_CALL_RESPONSE_CODES } from "../entities/PlpType";
import { getFieldByName } from "../utilities/ElementUtils";
import { FormSectionElements, FormSections } from "../entities/formTemplateDataType";
import NumericValueUtils from "../utilities/NumericValueUtils";
import { AbstractResponseHandler } from "./AbstractResponseHandler";

/**
 * To handle service-call responses in PLP.
 */
export class PlpResponseHandler implements AbstractResponseHandler {
  constructor() {}

  handle(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    const name = fieldOrSection instanceof FormSectionElements ? fieldOrSection.LoadBindName : fieldOrSection.ENUTitle;
    console.log('about to execute handle for ' + name + ' in PlpResponseHandler...');
    
    switch (name) {
      case 'Continue':
        return this.handleIdentificationLookup(response, fieldOrSection);
        break;
      case 'Review order':
        return this.handleReviewOrder(response, fieldOrSection);
        break;
      case 'Complete Payment':
        return this.handleCompletePayment(response, fieldOrSection);
        break;
      default:
        console.log('No handler for ' + name);
        break;
    }
  }

  private handleIdentificationLookup(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    const INVALID_DL_ENG = 'Invalid Driver\'s licence number.';
    const INVALID_DL_FR = 'Le numéro du permis de conduire n’est pas valide.';
    const LESS_CURRENT_DL_ENG = 'The Driver\'s Licence Number you entered is not the most current on file. Please enter your current number or visit your nearest ServiceOntario for support';
    const LESS_CURRENT_DL_FR = 'Le numéro de permis de conduire que vous avez saisi ne correspond pas au numéro actuel figurant au dossier. Veuillez saisir votre numéro actuel ou vous rendre au centre ServiceOntario le plus proche pour obtenir de l’aide.';

    const INVALID_RIN_ENG = 'Invalid Registrant identification number (RIN)';
    const INVALID_RIN_FR = 'Le numéro d’identification du titulaire n’est pas valide';
    const INVALID_RIN_TYPE_ENG = 'The RIN must be ‘I’ (Individual), ‘J’ (Joint) or ‘C’ (Company).”';
    const INVALID_RIN_TYPE_FR = 'Le NIT doit être « I » (Individu), « J » (Copropriété) ou « C » (Compagnie).';


    if ((response.statusCode === SERVICE_CALL_RESPONSE_CODES.RIN_DL_NOT_EXISTS) || (response === SERVICE_CALL_RESPONSE_CODES.RIN_DL_NOT_EXISTS)) {
      const identificationType = fieldOrSection.findFieldByName('identificationType');
      if (identificationType?.Value === 'Driver\'s licence number') {
        const dlInputElement = fieldOrSection.findFieldByName('driverLicenceNumber');
        if (dlInputElement) {
          dlInputElement.ErrorMessage = INVALID_DL_ENG;  // Reset to default invalid validation error msg.
          dlInputElement.ErrorMessageFRA = INVALID_DL_FR;
          dlInputElement.Touched = true;  // To show element-level error msg.
          dlInputElement.Valid = false;
        }
      } else {
        const rinInputElement = fieldOrSection.findFieldByName('registrantInformationNumber');
        if (rinInputElement) {
          rinInputElement.ErrorMessage = INVALID_RIN_ENG;  // Reset to default invalid validation error msg.
          rinInputElement.ErrorMessageFRA = INVALID_RIN_FR;
          rinInputElement.Touched = true;  // To show element-level error msg.
          rinInputElement.Valid = false;
        }
      }
      return { processSuccessful: false};
    } else if ((response.statusCode === SERVICE_CALL_RESPONSE_CODES.DL_NOT_MOST_CURRENT) || (response === SERVICE_CALL_RESPONSE_CODES.DL_NOT_MOST_CURRENT)) {  // REG013 error.
      const identificationType = fieldOrSection.findFieldByName('identificationType');
      if (identificationType?.Value === 'Driver\'s licence number') {
        const dlInputElement = fieldOrSection.findFieldByName('driverLicenceNumber');
        if (dlInputElement) {
          dlInputElement.ErrorMessage = LESS_CURRENT_DL_ENG;  // Set to specific error msg.
          dlInputElement.ErrorMessageFRA = LESS_CURRENT_DL_FR;
          dlInputElement.Touched = true;  // To show element-level error msg.
          dlInputElement.Valid = false;
        }
      }
      return { processSuccessful: false};
    } else if ((response.statusCode === SERVICE_CALL_RESPONSE_CODES.RIN_INVALID_TYPE) || (response === SERVICE_CALL_RESPONSE_CODES.RIN_INVALID_TYPE)) {
      const identificationType = fieldOrSection.findFieldByName('identificationType');
      if (identificationType?.Value === 'Registrant identification number (RIN)') {
        const rinInputElement = fieldOrSection.findFieldByName('registrantInformationNumber');
        if (rinInputElement) {
          rinInputElement.ErrorMessage = INVALID_RIN_TYPE_ENG;  // Set to specific error msg.
          rinInputElement.ErrorMessageFRA = INVALID_RIN_TYPE_FR;
          rinInputElement.Touched = true;  // To show element-level error msg.
          rinInputElement.Valid = false;
        }
      }
      return { processSuccessful: false};
    }
    return { processSuccessful: true};
  }

  private handleReviewOrder(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    if ((response.statusCode == SERVICE_CALL_RESPONSE_CODES.PROCESS_SUCCESSFUL) || (response == SERVICE_CALL_RESPONSE_CODES.PROCESS_SUCCESSFUL)) {
      const orderItemField = getFieldByName('orderItemDetails', fieldOrSection);
      if (orderItemField) {
        // Pass isEng to generateOrderBreakdown
           orderItemField.Value = {
            en: this.generateOrderBreakdown(response, orderItemField, true),
            fr: this.generateOrderBreakdown(response, orderItemField, false)
        };
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return { processSuccessful: true, stepIndex: 'Complete your payment' };  // Go to 'Complete your payment' page.
      }
    }
    return { processSuccessful: false, stepIndex: 'Review order' };  // Stay on 'Review order' page.
  }

  private generateOrderBreakdown(response: any, field: FormSectionElements | FormSections, isEng: Boolean): any {
   const plateTypeMapEnglish = {
      PASSENGER: 'Personalized passenger plate',
      COMMERCIAL: 'Personalized commercial plate',
      MOTORCYCLE: 'Personalized motorcycle plate'
    };
    const plateTypeMapFrench = {
      PASSENGER: 'Plaque personnalisée pour véhicule de tourisme',
      COMMERCIAL: 'Plaque personnalisée pour véhicule utilitaire',
      MOTORCYCLE: 'Plaque personnalisée pour motocyclette'
    };
  
    const orderItemLines = [];
    response.priceBreakdown.map((orderItem) => {
      const withGraphic = (orderItem.plateInfo.graphicType && orderItem.plateInfo.graphicType !== '0000');
      const itemDescription1 = (isEng ? 
        plateTypeMapEnglish[orderItem.plateInfo.plateType] + (withGraphic ? ' with graphic' : '') : 
        plateTypeMapFrench[orderItem.plateInfo.plateType] + (withGraphic ? ' - avec élément graphique' : ''));
      const itemAmount1 = (isEng ? NumericValueUtils.toAmountInEnglish(orderItem.totalItemCost.unitCost) :
        NumericValueUtils.toAmountInFrench(orderItem.totalItemCost.unitCost));
      const itemDescription2 = (isEng ? 'Item details: ' : 'Détails de l\’article : ') + (orderItem.plateInfo.plateCharacters);
      orderItemLines.push({itemDescription1, itemAmount1, itemDescription2});
    })

    const subTotalDescription = (isEng ? 'Subtotal' : 'Total partiel');
    let subTotal = 0;
    response.priceBreakdown.map((orderItem) => {
      subTotal += orderItem.totalItemCost.unitCost ? Number(orderItem.totalItemCost.unitCost) : 0;
    })
    const subTotalAmount = (isEng ? NumericValueUtils.toAmountInEnglish(subTotal) : NumericValueUtils.toAmountInFrench(subTotal));
    const subTotalLine = { subTotalDescription, subTotalAmount };
    
    let totalTax = 0;
    let taxRate = 0;
    response.priceBreakdown.map((orderItem) => {
      taxRate = orderItem.totalItemCost.taxRate ? Number(orderItem.totalItemCost.taxRate) : 0;
      totalTax += orderItem.totalItemCost.taxAmount ? Number(orderItem.totalItemCost.taxAmount) : 0;
    })
    const taxDescription = (isEng ? 'HST(' + NumericValueUtils.toPercentageInEnglish(taxRate) + ')' :
      'TVH(' + NumericValueUtils.toPercentageInFrench(taxRate) + ')');
    const totalTaxAmount = (isEng ? NumericValueUtils.toAmountInEnglish(totalTax) :
      NumericValueUtils.toAmountInFrench(totalTax))
    const totalTaxLine = { taxDescription, totalTaxAmount };

    const totalOrderAmountField = getFieldByName('totalOrderAmount', field);
    totalOrderAmountField.Value = response.totalAmount;  // Saving the total order amount.
    
    const taxAmountDescription = (isEng ? 'Total' : 'Total');
    const totalAmount = (isEng ? NumericValueUtils.toAmountInEnglish(response.totalAmount) :
      NumericValueUtils.toAmountInFrench(response.totalAmount))
    const totalAmountLine = { taxAmountDescription, totalAmount };

    return { orderItemLines, subTotalLine, totalTaxLine, totalAmountLine };
  }

  private handleCompletePayment(response: any, fieldOrSection: FormSectionElements | FormSections): any {
    if ((response.statusCode == SERVICE_CALL_RESPONSE_CODES.COMPLETE_PAYMENT_SUCCESSFUL) || (response == SERVICE_CALL_RESPONSE_CODES.COMPLETE_PAYMENT_SUCCESSFUL)) {
      return response.data?.providerUrl;
    } else {
      return null;
    }
  }
}